/**
 * Página exibida na aba nova enquanto o PDF do checklist é gerado (oficina/at-139).
 *
 * A aba nasce vazia (`window.open('')`, síncrono, para não cair no bloqueador de pop-up) e não
 * carrega o CSS da aplicação: por isso tudo aqui é inline. A porca do logo do SOAC gira como
 * indicador de espera; com `prefers-reduced-motion`, ela pulsa em vez de girar.
 *
 * ⚠️ A página não promete tempo: a geração depende da quantidade de fotos, e não há medição
 * que sustente um número.
 */
export function paginaGerandoPdf(placa?: string | null): string {
  const placaSegura = escaparHtml((placa || '').toUpperCase());
  const blocoDaPlaca = placaSegura
    ? `<div class="placa"><span class="placa-faixa">BRASIL</span><span class="placa-numero">${placaSegura}</span></div>`
    : '';

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Gerando o PDF do checklist${placaSegura ? ` - ${placaSegura}` : ''}</title>
<style>
  :root { --fundo: #f6f4f2; --cartao: #ffffff; --texto: #2b2b2b; --texto-2: #6b6b6b; --borda: #ece7e3; --marca: #ea5d0e; }
  @media (prefers-color-scheme: dark) {
    :root { --fundo: #14171c; --cartao: #1d2128; --texto: #eceff3; --texto-2: #a3abb6; --borda: #2c323b; }
  }
  * { box-sizing: border-box; }
  html, body { height: 100%; margin: 0; }
  body {
    background: var(--fundo); color: var(--texto);
    font-family: "Segoe UI", system-ui, -apple-system, Roboto, Arial, sans-serif;
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .cartao {
    background: var(--cartao); border: 1px solid var(--borda); border-top: 4px solid var(--marca);
    border-radius: 14px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    width: 100%; max-width: 420px; padding: 36px 28px 30px; text-align: center;
  }
  .porca { display: block; width: 64px; height: 64px; margin: 0 auto 22px; animation: girar 1.4s linear infinite; }
  @keyframes girar { to { transform: rotate(360deg); } }
  @keyframes pulsar { 50% { opacity: 0.45; } }
  @media (prefers-reduced-motion: reduce) { .porca { animation: pulsar 1.6s ease-in-out infinite; } }
  h1 { font-size: 20px; font-weight: 600; margin: 0 0 8px; }
  p { font-size: 14px; line-height: 1.5; color: var(--texto-2); margin: 0; }
  .placa {
    display: flex; flex-direction: column; align-items: stretch; margin: 0 auto 20px;
    width: 132px; border: 1.5px solid #1b1b1b; border-radius: 6px; overflow: hidden; background: #fff;
  }
  .placa-faixa { background: #1f4fa3; color: #fff; font-size: 8px; letter-spacing: 1.5px; padding: 2px 0; font-weight: 600; }
  .placa-numero { color: #1b1b1b; font-size: 22px; font-weight: 700; letter-spacing: 2px; padding: 4px 0 5px; }
  .barra { height: 4px; border-radius: 2px; background: var(--borda); overflow: hidden; margin-top: 24px; }
  .barra span { display: block; height: 100%; width: 35%; background: var(--marca); border-radius: 2px; animation: correr 1.6s ease-in-out infinite; }
  @keyframes correr { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
  @media (prefers-reduced-motion: reduce) { .barra span { animation: none; width: 100%; opacity: 0.5; } }
</style>
</head>
<body>
  <main class="cartao" role="status" aria-live="polite">
    <svg class="porca" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path d="M24 2 L43 13 V35 L24 46 L5 35 V13 Z" fill="#ea5d0e" />
      <circle cx="24" cy="24" r="9" fill="var(--cartao)" />
    </svg>
    ${blocoDaPlaca}
    <h1>Gerando o PDF do checklist</h1>
    <p>Estamos reunindo os itens e as fotos da vistoria. O documento abre nesta aba assim que ficar pronto.</p>
    <div class="barra" aria-hidden="true"><span></span></div>
  </main>
</body>
</html>`;
}

function escaparHtml(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
